import { renderHeatOption, useHeats } from "@/shared/selectors/HeatSelector";
import { inchesToParts, partsToInches } from "@/shared/utils/length";
import { getMomentTz } from "@dexteel/mesf-core";
import { Check, Close } from "@mui/icons-material";
import {
	Autocomplete,
	Box,
	InputBase,
	type InputBaseProps,
	MenuItem,
	Select,
	TextField,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { useRef, useState } from "react";

const moment = getMomentTz();

const SELECT_SX = {
	fontSize: 11,
	width: "100%",
	"& .MuiSelect-select": { py: 0, pr: "18px !important", textAlign: "center" },
	"& .MuiSelect-icon": { fontSize: 16, right: 0 },
} as const;

type CellInputProps = Omit<InputBaseProps, "sx"> & {
	align?: "center" | "right";
	sx?: Record<string, unknown>;
};

const CellInput = ({ align = "center", sx, ...props }: CellInputProps) => (
	<InputBase
		autoComplete="off"
		onFocus={(event) => event.target.select()}
		sx={{
			fontSize: 11,
			width: "100%",
			"& input": { p: 0, textAlign: align },
			...sx,
		}}
		{...props}
	/>
);

export const PassFailMark = ({ value }: { value: boolean }) => {
	const sx = { fontSize: 15, verticalAlign: "middle" };

	return value ? <Check sx={sx} /> : <Close sx={sx} />;
};

export const passFailSx = (value: boolean | null) => {
	if (value === null) return null;

	return { color: value ? "success.main" : "error.main", fontWeight: 600 };
};

type CellProps<T> = {
	value: T;
	onChange: (value: T) => void;
};

const NUMBER_PATTERN = /^-?\d{0,5}(\.\d{0,4})?$/;

export const NumberCell = ({ value, onChange }: CellProps<string>) => (
	<CellInput
		align="right"
		value={value}
		inputProps={{ inputMode: "decimal" }}
		onChange={(event) => {
			if (NUMBER_PATTERN.test(event.target.value)) onChange(event.target.value);
		}}
	/>
);

const Unit = ({ children }: { children: string }) => (
	<Box
		component="span"
		sx={{ fontSize: 9, color: "text.secondary", ml: "1px" }}
	>
		{children}
	</Box>
);

export const LengthCell = ({ value, onChange }: CellProps<string>) => {
	const feetRef = useRef<HTMLInputElement>(null);
	const inchesRef = useRef<HTMLInputElement>(null);
	const [parts, setParts] = useState(() =>
		inchesToParts(value === "" ? null : Number(value)),
	);

	const emit = (feet: string, inches: string) => {
		const total = partsToInches(feet, inches);

		setParts({ feet, inches });
		onChange(total === null ? "" : String(total));
	};

	return (
		<Box
			onMouseDown={(event) => {
				if (event.target !== event.currentTarget) return;

				event.preventDefault();
				if (!feetRef.current) return false;

				feetRef.current.focus();
				if (feetRef.current instanceof HTMLInputElement)
					feetRef.current.select();

				return true;
			}}
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				gap: "4px",
				cursor: "text",
				height: "100%",
				mx: -0.5,
				px: 0.5,
			}}
		>
			<CellInput
				inputRef={feetRef}
				align="right"
				value={parts.feet}
				placeholder="0"
				inputProps={{ inputMode: "numeric", "aria-label": "feet" }}
				endAdornment={<Unit>{"'"}</Unit>}
				onChange={(event) => {
					const feet = event.target.value
						.replace(/[^0-9]/g, "")
						.replace(/^0+(?=\d)/, "");

					if (feet.length <= 3) emit(feet, parts.inches);
				}}
				onKeyDown={(event) => {
					if (event.key === "'" || event.key === " ") {
						event.preventDefault();
						inchesRef.current?.focus();
					}
				}}
				sx={{
					flex: "0 0 auto",
					width: "auto",
					"& input": {
						p: 0,
						textAlign: "right",
						width: `${Math.max(parts.feet.length, 1)}ch`,
						"&::placeholder": { color: "text.secondary", opacity: 1 },
					},
				}}
			/>
			<CellInput
				inputRef={inchesRef}
				align="right"
				value={parts.inches}
				placeholder="0"
				inputProps={{ inputMode: "numeric", "aria-label": "inches" }}
				endAdornment={<Unit>{'"'}</Unit>}
				onChange={(event) => {
					const inches = event.target.value
						.replace(/[^0-9]/g, "")
						.replace(/^0+(?=\d)/, "");

					if (Number(inches) <= 11) emit(parts.feet, inches);
				}}
				sx={{
					flex: "0 0 auto",
					width: "auto",
					"& input": {
						p: 0,
						textAlign: "right",
						width: `${Math.max(parts.inches.length, 1)}ch`,
						"&::placeholder": { color: "text.secondary", opacity: 1 },
					},
				}}
			/>
		</Box>
	);
};

export const TextCell = ({ value, onChange }: CellProps<string>) => (
	<CellInput value={value} onChange={(event) => onChange(event.target.value)} />
);

export const TimestampCell = ({ value, onChange }: CellProps<Date | null>) => (
	<DateTimePicker
		value={value ? moment(value) : null}
		format="MM/DD/YY HH:mm"
		views={["year", "month", "day", "hours", "minutes"]}
		ampm={false}
		disableFuture
		onChange={(next) => {
			if (next?.isValid()) {
				onChange(moment.min(next, moment()).toDate());
			}
		}}
		slotProps={{
			textField: {
				variant: "standard",
				fullWidth: true,
				inputProps: { "data-sections": "" },
				InputProps: {
					disableUnderline: true,
					sx: {
						fontSize: 11,
						justifyContent: "center",
						"& input": {
							flex: "none",
							p: 0,
							textAlign: "right",
							width: "14ch",
						},
						"& .MuiInputAdornment-root": { ml: 0 },
					},
				},
			},
			openPickerButton: { size: "small", sx: { p: 0.25 } },
			openPickerIcon: { sx: { fontSize: 14 } },
		}}
	/>
);

export const DateTimeCell = ({ value, onChange }: CellProps<Date | null>) => (
	<CellInput
		type="datetime-local"
		value={value ? moment(value).format("YYYY-MM-DDTHH:mm") : ""}
		onChange={(event) => {
			if (!event.target.value) return onChange(null);

			const parsed = moment(event.target.value, "YYYY-MM-DDTHH:mm");

			if (parsed.isValid()) onChange(parsed.toDate());
		}}
		sx={{
			"& input::-webkit-calendar-picker-indicator": { display: "none" },
		}}
	/>
);

type PassFailOption = "" | "GO" | "NO GO";

export const PassFailCell = ({
	value,
	onChange,
}: CellProps<boolean | null>) => (
	<Select<PassFailOption>
		variant="standard"
		disableUnderline
		displayEmpty
		value={value === null ? "" : value ? "GO" : "NO GO"}
		onChange={(event) =>
			onChange(event.target.value === "" ? null : event.target.value === "GO")
		}
		renderValue={(selected) =>
			selected === "" ? "-" : <PassFailMark value={selected === "GO"} />
		}
		sx={{ ...SELECT_SX, ...passFailSx(value) }}
	>
		<MenuItem value="">-</MenuItem>
		<MenuItem value="GO">GO</MenuItem>
		<MenuItem value="NO GO">NO GO</MenuItem>
	</Select>
);

export const HeatCell = ({
	value,
	sectionId,
	onChange,
}: CellProps<number | null> & { sectionId: number | null }) => {
	const { data: heats } = useHeats({
		refetchInterval: 60_000,
		sectionId: sectionId ?? undefined,
	});

	return (
		<Autocomplete
			size="small"
			autoHighlight
			forcePopupIcon={false}
			options={heats ?? []}
			value={heats?.find((heat) => heat.HeatId === value) ?? null}
			getOptionLabel={(heat) => heat.HeatName}
			isOptionEqualToValue={(option, item) => option.HeatId === item.HeatId}
			onChange={(_event, heat) => onChange(heat?.HeatId ?? null)}
			renderOption={renderHeatOption}
			slotProps={{
				popper: { placement: "bottom-start", style: { width: "auto" } },
				paper: {
					sx: {
						fontSize: 12,
						width: "max-content",
						minWidth: 260,
						maxWidth: 380,
					},
				},
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					variant="standard"
					placeholder="Heat"
					InputProps={{
						...params.InputProps,
						disableUnderline: true,
						sx: { fontSize: 11, py: "0 !important" },
					}}
				/>
			)}
			sx={{
				"& .MuiAutocomplete-endAdornment": {
					right: 0,
					top: "50%",
					transform: "translateY(-50%)",
					"& .MuiAutocomplete-clearIndicator": { p: 0 },
					"& svg": { fontSize: 14 },
				},
			}}
		/>
	);
};
